import React, { useEffect, useRef } from 'react';
export const findNextEnabledIndex = ({ totalItems, isItemDisabled = () => false, currentIndex, direction, }) => {
    // if all items are disabled, return the current index
    if (Array(totalItems)
        .fill(1)
        .every((_item, index) => isItemDisabled(index))) {
        return currentIndex;
    }
    const indexChange = direction === 'next' ? 1 : -1;
    let nextEnabledIndex = currentIndex + indexChange;
    // wrap the next index around the array both ways
    if (nextEnabledIndex < 0) {
        nextEnabledIndex = totalItems - 1;
    }
    if (nextEnabledIndex === totalItems) {
        nextEnabledIndex = 0;
    }
    if (isItemDisabled(nextEnabledIndex)) {
        return findNextEnabledIndex({
            totalItems,
            isItemDisabled,
            currentIndex: nextEnabledIndex,
            direction,
        });
    }
    return nextEnabledIndex;
};
/** A hook that returns correct accessibility props and keyboard controls for a popover menu.  Based on this article:
 * https://www.smashingmagazine.com/2017/11/building-accessible-menu-systems/#keyboard-and-focus-behavior
 */
export const useMenu = ({ isOpen, setIsOpen, isItemDisabled, totalItems, }) => {
    const menuItemRefs = useRef(Array.from(Array(totalItems), () => React.createRef()));
    const [highlightedIndex, setHighlightedIndex] = React.useState(() => findNextEnabledIndex({
        totalItems,
        isItemDisabled,
        currentIndex: -1,
        direction: 'next',
    }));
    // focus on highlighted item
    useEffect(() => {
        if (menuItemRefs.current?.[highlightedIndex] && isOpen) {
            menuItemRefs.current?.[highlightedIndex]?.current?.focus({ preventScroll: true });
        }
    }, [isOpen, menuItemRefs, highlightedIndex]);
    const handleKeyDown = (event) => {
        event.preventDefault();
        // enter is handled by the onClick on the menuitem
        if (event.code === 'ArrowDown') {
            setHighlightedIndex((index) => {
                const nextIndex = findNextEnabledIndex({ totalItems, isItemDisabled, currentIndex: index, direction: 'next' });
                return nextIndex;
            });
        }
        if (event.code === 'ArrowUp') {
            setHighlightedIndex((index) => {
                const previousIndex = findNextEnabledIndex({
                    totalItems,
                    isItemDisabled,
                    currentIndex: index,
                    direction: 'previous',
                });
                return previousIndex;
            });
        }
    };
    const handleOpenWithItemHighlighted = (item) => {
        setIsOpen(true);
        const firstIndex = findNextEnabledIndex({
            totalItems,
            isItemDisabled,
            currentIndex: item === 'first' ? -1 : totalItems,
            direction: item === 'first' ? 'next' : 'previous',
        });
        setHighlightedIndex(firstIndex);
    };
    return {
        getMenuProps: (props) => {
            const { onKeyDown } = props ?? {};
            return {
                onKeyDown: (event) => {
                    handleKeyDown(event);
                    onKeyDown?.(event);
                },
                role: 'menu',
                'aria-orientation': 'vertical',
            };
        },
        getTriggerProps: () => ({
            onKeyDown: (event) => {
                if (event.code === 'ArrowDown' || event.code === 'Enter' || event.code === 'Space') {
                    event.preventDefault();
                    handleOpenWithItemHighlighted('first');
                }
                if (event.code === 'ArrowUp') {
                    event.preventDefault();
                    handleOpenWithItemHighlighted('last');
                }
            },
            onClick: () => {
                if (!isOpen) {
                    handleOpenWithItemHighlighted('first');
                }
                else {
                    setIsOpen(false);
                }
            },
        }),
        getItemProps: (index, { onClick, onKeyDown, }) => !isItemDisabled(index)
            ? {
                ref: menuItemRefs?.current?.[index],
                role: 'menuitem',
                tabIndex: highlightedIndex === index ? 0 : -1,
                onClick: (event) => {
                    setIsOpen(false);
                    onClick?.(event);
                },
                isHighlighted: index === highlightedIndex,
                onMouseOver: () => setHighlightedIndex(index),
                onKeyDown: (event) => {
                    if (event.code === 'Enter' || event.code === 'Space') {
                        event?.currentTarget?.click();
                    }
                    if (onKeyDown) {
                        onKeyDown(event);
                    }
                },
            }
            : { tabIndex: -1 },
        highlightedIndex,
    };
};
